import React, { useCallback, useEffect, useMemo } from 'react';

import {
  useBrokenFlowModal,
  useFlow,
  useTrackPage,
  useTracking,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import {
  Body,
  Box,
  Button,
  Glyph,
  Heading,
  SystemModal,
  SystemModalContent,
  SystemModalHeader,
  SystemModalIcon,
} from '@edreamsodigeo/prisma-design-system';

import { DATA_MESH_IDS } from '#src/utils/constants';

import {
  GOBACK_EVENT,
  RETRY_EVENT,
  WAITING_PAGE,
  reloadThePage,
} from './utils';
import { DataMeshTrackedComponent } from '../DataMeshTrackedComponent';

interface ErrorModalProps {
  onClose: () => void;
  onRetry: () => void;
  open: boolean;
}

export const ErrorModal = ({ onClose, onRetry, open }: ErrorModalProps) => {
  const { t } = useTranslation();
  const flow = useFlow();
  const isDesktop = useMemo(() => flow === 'desktop', [flow]);

  return (
    <SystemModal
      id="ap-product-funnel-error-modal"
      modalSize="medium"
      onClose={onClose}
      open={open}
    >
      <SystemModalHeader type="warning">
        <SystemModalIcon>
          <Glyph category="Alerts" color="warning.50" name="Warning" />
        </SystemModalIcon>
      </SystemModalHeader>
      <SystemModalContent>
        <Box
          pb={isDesktop ? 'base' : '1x_small'}
          px={isDesktop ? '1x_big' : 'tiny'}
        >
          <Box pb={isDesktop ? 'base' : 'big'} pt="tiny">
            <Heading color="neutral.90" textAlign="center" type="2">
              {t('errormodal.generic.title')}
            </Heading>
          </Box>
          <Box pb={isDesktop ? '1x_big' : 'big'}>
            <Body color="neutral.80" textAlign="center" type="base">
              {t('errormodal.generic.description')}
            </Body>
          </Box>
          <Box pb="base">
            <DataMeshTrackedComponent click id={DATA_MESH_IDS.ERROR_MODAL} load>
              <Button fullWidth onClick={onClose}>
                {t('errormodal.continueBtn')}
              </Button>
            </DataMeshTrackedComponent>
          </Box>
          <DataMeshTrackedComponent
            click
            id={DATA_MESH_IDS.ERROR_MODAL_TRY_AGAIN}
          >
            <Button fullWidth onClick={onRetry} type="secondary">
              {t('errormodal.tryAgainBtn')}
            </Button>
          </DataMeshTrackedComponent>
        </Box>
      </SystemModalContent>
    </SystemModal>
  );
};

export interface BrokenFlowErrorModalProps {
  redirectTo?: string;
}

const hideWaitingPage = () => {
  const waitingPage = document.getElementById(WAITING_PAGE);
  if (waitingPage) {
    waitingPage.style.display = 'none';
  }
};

export const BrokenFlowErrorModal = ({
  redirectTo = '/travel/secure/#tripdetails/',
}: BrokenFlowErrorModalProps) => {
  const { close, isOpen } = useBrokenFlowModal();
  const { trackEvent } = useTracking();
  const { trackGAPage } = useTrackPage();

  const onRetry = useCallback(async () => {
    await trackEvent(RETRY_EVENT);
    reloadThePage();
  }, [trackEvent]);

  const onClose = useCallback(async () => {
    close();
    await trackEvent(GOBACK_EVENT);
    window.location.assign(redirectTo);
  }, [close, trackEvent, redirectTo]);

  useEffect(() => {
    if (isOpen) {
      trackGAPage('/PB/fast-track/payment/error/', { funnel: false });
      hideWaitingPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return <ErrorModal onClose={onClose} onRetry={onRetry} open={isOpen} />;
};
